/** Neutral, factual product language for the ticker / macro / scanner previews.
 * Display copy only. No pricing, validation or screen logic lives here.
 */

export const SCREEN = {
  clear: {
    key: "clear",
    full: "No current screen constraint",
    compact: "No constraint",
    headline: "Income screen: no current screen constraint",
    tier: "info",
  },
  flag: {
    key: "flag",
    full: "Data or event flag",
    compact: "Data/event flag",
    headline: "Income screen status: data/event flag",
    tier: "caution",
  },
  unavailable: {
    key: "unavailable",
    full: "Not available under current screen conditions",
    compact: "Not available",
    headline: "Not available under current screen conditions",
    tier: "blocking",
  },
  preparing: {
    key: "preparing",
    full: "Preparing data",
    compact: "Preparing",
    headline: "Data preparation in progress",
    tier: "info",
  },
};

export const BASKET = {
  available: "Basket action available",
  unavailable: "Basket action unavailable",
};

export const REASON = {
  quoteFields: "Required quote fields are unavailable",
  timestamp: "Option quote timestamp is unavailable",
  indicative: "Indicative estimate only",
  earningsInside: "Confirmed earnings event falls inside selected time window",
  earningsPredicted: "Earnings date is predicted, not confirmed",
  earningsExpectedInside: "Expected earnings date falls inside selected time window",
  liquidity: "Does not meet current liquidity parameters",
  portfolio: "Outside current portfolio parameters",
  structure: "Structure does not meet current parameters",
  preparing: "Data preparation in progress",
  noData: "Data unavailable",
  noStructure: "No structure shown under current screen conditions",
};

export const DATA_LAYER = {
  observed: { text: "Observed pricing available", tier: "info", mark: "●" },
  quoteFields: { text: "Required quote fields unavailable", tier: "blocking", mark: "!" },
  timestamp: { text: "Option quote timestamp unavailable", tier: "blocking", mark: "!" },
  indicative: { text: "Indicative estimate only", tier: "caution", mark: "≈" },
  pricingUnavailable: { text: "Pricing unavailable", tier: "blocking", mark: "!" },
};

export const EVENT_LAYER = {
  clear: { text: "No confirmed earnings event in selected time window", tier: "info", mark: "●" },
  earningsInside: { text: "Confirmed earnings event falls inside selected time window", tier: "caution", mark: "▲" },
  earningsExpectedInside: { text: "Expected earnings date falls inside selected time window", tier: "caution", mark: "▲" },
  earningsPredicted: { text: "Earnings date is predicted, not confirmed", tier: "caution", mark: "▲" },
  earningsUnknown: { text: "Earnings date unavailable", tier: "caution", mark: "▲" },
  liquidity: { text: "Outside current liquidity parameters", tier: "caution", mark: "▲" },
  portfolio: { text: "Outside current portfolio parameters", tier: "caution", mark: "▲" },
};

export const REGIME_CONTEXT = {
  available: "Historical regime context available",
  differs: "Historical regime context differs",
  limited: "Historical regime context limited",
  insufficient: "Insufficient historical sample",
};

export const FIELD = {
  observed: "Observed",
  incomplete: "Incomplete",
  unavailable: "Unavailable",
  indicative: "Indicative",
  notConfigured: "Not configured",
  notInOutput: "Not available in this output",
  within: "Within current parameters",
  outside: "Outside current parameters",
  overlap: "Event overlap",
  predicted: "Earnings date predicted, not confirmed",
  notEvaluated: "Not evaluated in this preview",
  informational: "Informational only",
};

export const RESEARCH = {
  available: "Historical context available",
  limited: "Historical context limited",
  mixed: "Mixed context",
  reference: "Reference only",
  insufficient: "Insufficient sample",
  unavailable: "Unavailable",
  notConfigured: "Not configured",
};

export const HELPER = {
  screenFilter:
    "Screen states reflect currently available data and configured parameters. They are not a recommendation, suitability assessment, or instruction to trade.",
  overview:
    "This summary reflects available data and configured MarkovLab screen parameters. It is not investment advice or a recommendation.",
  evidence:
    "Historical and regime-conditioned observations describe prior results. They are not a forecast, expected return, recommendation, suitability assessment, or executable trade quote.",
  negativeHist: "Historical result shown for context; not a forecast.",
  conditions:
    "Evidence and conditions describe available data, historical observations and configured parameters. They are not investment advice, a suitability assessment, a recommendation, or an instruction to trade.",
  analyst:
    "Analyst targets are third-party estimates, not a valuation floor, price forecast, strategy approval criterion, or recommendation.",
  colour:
    "Blue/grey is informational or observed. Amber is a data, event or parameter flag. Red is an unavailable field or unavailable product action. Green is only an observed or completed data state.",
};

export const INCOME_FILTERS = [
  { value: "all", compact: "All", full: "All" },
  { value: "clear", compact: SCREEN.clear.compact, full: SCREEN.clear.full },
  { value: "flag", compact: SCREEN.flag.compact, full: SCREEN.flag.full },
  { value: "preparing", compact: SCREEN.preparing.compact, full: SCREEN.preparing.full },
  { value: "unavailable", compact: SCREEN.unavailable.compact, full: SCREEN.unavailable.full },
];

export function screenOf(key) {
  return SCREEN[key] || SCREEN.flag;
}

export function dataLayerOf(basis) {
  if (basis === "unverified") return { key: "timestamp", ...DATA_LAYER.timestamp };
  if (basis === "unavailable") return { key: "pricingUnavailable", ...DATA_LAYER.pricingUnavailable };
  if (basis === "partial") return { key: "quoteFields", ...DATA_LAYER.quoteFields };
  if (basis === "modelled" || basis === "lastPrice") return { key: "indicative", ...DATA_LAYER.indicative };
  return { key: "observed", ...DATA_LAYER.observed };
}

export function eventLayerOf(conflict) {
  if (!conflict) return { key: "clear", ...EVENT_LAYER.clear };
  if (conflict.state === "inside" && conflict.quality === "confirmed") {
    return { key: "earningsInside", ...EVENT_LAYER.earningsInside };
  }
  if (conflict.state === "inside") {
    return { key: "earningsExpectedInside", ...EVENT_LAYER.earningsExpectedInside };
  }
  if (conflict.state === "predicted") return { key: "earningsPredicted", ...EVENT_LAYER.earningsPredicted };
  if (conflict.state === "unavailable") return { key: "earningsUnknown", ...EVENT_LAYER.earningsUnknown };
  return { key: "clear", ...EVENT_LAYER.clear };
}

export function actionLayerOf(dataKey) {
  const unavailable = dataKey === "timestamp" || dataKey === "pricingUnavailable" || dataKey === "quoteFields";
  return {
    key: unavailable ? "unavailable" : "available",
    text: unavailable ? BASKET.unavailable : BASKET.available,
    tier: unavailable ? "blocking" : "info",
    mark: unavailable ? "!" : "●",
  };
}

export function screenFromLayers(dataKey, eventKey) {
  if (dataKey === "timestamp" || dataKey === "pricingUnavailable" || dataKey === "quoteFields") {
    return "unavailable";
  }
  if (eventKey !== "clear" || dataKey === "indicative") return "flag";
  return "clear";
}

export function reasonForScreen(dataKey, eventKey) {
  if (dataKey === "timestamp") return REASON.timestamp;
  if (dataKey === "quoteFields") return REASON.quoteFields;
  if (dataKey === "pricingUnavailable") return REASON.noData;
  if (dataKey === "indicative") return REASON.indicative;
  if (eventKey === "earningsInside") return REASON.earningsInside;
  if (eventKey === "earningsExpectedInside") return REASON.earningsExpectedInside;
  if (eventKey === "earningsPredicted") return REASON.earningsPredicted;
  return SCREEN.clear.full;
}
